import APIS from 'src/constants/ApiConfig';
import { IPageResult } from 'src/entity/BaseResult';
import { IArea, IAreaInfo, ILocationInfo } from 'src/entity/LocationEntity';
import HttpService from 'src/services/HttpService';

/**
 * 地区Service
 * @class LocationService
 */
class LocationService {
  /**
   * key:省份国家地区id，value:省份国家地区对象
   * @type {Map<number, ILocationInfo>}
   * @memberof LocationService
   */
  locationMap: Map<number, ILocationInfo> = new Map<number, ILocationInfo>();
  /**
   * 所有省市区详情
   * key: 地区id，value: 地区对象
   * @memberof LocationService
   */
  areaMap: Map<number, IAreaInfo> = new Map<number, IAreaInfo>();

  /**
   * 省List
   * @memberof LocationService
   */
  linkageProvinceList: IAreaInfo[] = [];

  /**
   * 联动市/区Map
   * key: parentval, value: 市/区List
   * @memberof LocationService
   */
  linkageCityMap: Map<number, IAreaInfo[]> = new Map<number, IAreaInfo[]>();

  /**
   * 获取所有省份国家地区
   * 第一次获取以后保存到provinceList中，以后如果provinceList有则直接返回
   * @memberof LocationService
   */
  getAllLocation = (isDomestic: boolean = true): Promise<ILocationInfo[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        let tempList: ILocationInfo[] = [];
        this.locationMap.clear();
        if (!!isDomestic) {
          const result: IPageResult<ILocationInfo[]> = await HttpService.get(APIS.domainUrl(APIS.province));
          if (!!result && !!result.results) {
            tempList = result.results;
          }
        } else {
          tempList = (await HttpService.get(APIS.domainUrl(APIS.country))) || [];
        }
        for (const obj of tempList) {
          this.locationMap.set(obj.id, obj);
        }
        resolve(tempList);
      } catch (err) {
        reject(err);
      }
    });
  };

  /**
   * 根据id获取省份国家地区名称
   * @memberof LocationService
   */
  getLocationName = (id: number): string => {
    if (this.locationMap.has(id)) {
      const obj: ILocationInfo | undefined = this.locationMap.get(id);
      return !!obj ? obj.name : '';
    }
    return '';
  };

  /**
   * 初始化省市区三级联动数据
   * @memberof LocationService
   */
  initArea = async (): Promise<void> => {
    try {
      if (this.areaMap.size === 0) {
        this.areaMap.clear();
        this.linkageProvinceList = [];
        this.linkageCityMap.clear();
        const result: IArea[] = await HttpService.get(APIS.domainUrl(APIS.locationArea));
        if (!!result && result.length === 3) {
          const provinceList: IAreaInfo[] = result[0].options;
          const cityList: IAreaInfo[] = result[1].options;
          const districtList: IAreaInfo[] = result[2].options;

          for (const obj of provinceList) {
            this.areaMap.set(obj.value, obj);
            if (this.linkageProvinceList.findIndex(t => t.value === obj.value) < 0) {
              this.linkageProvinceList.push(obj);
            }
          }
          for (const obj of cityList) {
            this.areaMap.set(obj.value, obj);
            const parentVal: number = obj.parentVal as number;
            if (this.linkageCityMap.has(parentVal)) {
              const data: IAreaInfo[] = this.linkageCityMap.get(parentVal) || [];
              if (data.findIndex(t => t.value === obj.value) < 0) {
                data.push(obj);
              }
            } else {
              this.linkageCityMap.set(parentVal, [obj]);
            }
          }
          for (const obj of districtList) {
            this.areaMap.set(obj.value, obj);
            const parentVal: number = obj.parentVal as number;
            if (this.linkageCityMap.has(parentVal)) {
              const data: IAreaInfo[] = this.linkageCityMap.get(parentVal) || [];
              if (data.findIndex(t => t.value === obj.value) < 0) {
                data.push(obj);
              }
            } else {
              this.linkageCityMap.set(parentVal, [obj]);
            }
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * 获取省市区联动列表
   * @memberof LocationService
   */
  getLinkageProvince = (): IAreaInfo[] => {
    return this.linkageProvinceList;
  };

  /**
   * 根据parentId获取省市区联动列表
   * @memberof LocationService
   */
  getLinkageAreaByParentId = (parentId: number): IAreaInfo[] => {
    return this.linkageCityMap.get(parentId) || [];
  };

  /**
   * 根据id获取地区链 => [selfInfo, superAreaInfo1, superAreaInfo2 ...]
   */
  getAreaChain(areaId: number, areaChain: IAreaInfo[]): IAreaInfo[] {
    const currentArea = this.areaMap.get(areaId);
    if (!!currentArea) {
      areaChain.push(currentArea);
      if (!!currentArea.parentVal) {
        this.getAreaChain(currentArea.parentVal, areaChain);
      }
    }
    return areaChain.reverse();
  }

  /**
   * 根据传入的地区id查找名称
   */
  getLinkageAreaName(areaId: number): string {
    const areaItem = this.areaMap.get(areaId);
    return !!areaItem ? areaItem.text : '';
  }
}

export default new LocationService();



// WEBPACK FOOTER //
// ./src/services/LocationService.ts